import * as React from 'react';
import { StoryAnalyzerFeedbackForm } from '../../../components/PostDraft/storyAnalysis/story-analysis-feedback-form';
import BasicTabs from '../../../components/Tabs';
import SimpleBackdrop from '../../../components/UI/SimpleBackdrop';
import { DRAFT } from '../../../constants/draft.constants';
import { POST } from '../../../constants/post.constants';
import { AnalysisResponse } from '../../../interfaces/analysisResponse';
import { SentimentResponse } from '../../../interfaces/sentimentResponse';
import DialogHeader from './DialogHeader';
import { getScoreColor } from './helpers/getScoreColor';
import QuickSummary from './QuickSummary';
import { SafetyPanel } from './SafetyPanel';
import SentimentAnalysis from './SentimentAnalysis';
import SentimentDisplay from './SentimentScores';
import TopEmotions from './topEmotions';
import './text-analysis-drawer.scss';

const DRAWER_WIDTH = 480;

interface TextAnalysisResponseProps {
  isModalOpen: boolean;
  hasStoryAnalysis: boolean;
  isReAnalyze?: boolean;
  setShowAnalysisModal: React.Dispatch<React.SetStateAction<boolean>>;
  handleClickClose: () => Promise<void>;
  // Optional: used by the "Revise story" action (self-harm PROMOTION block) to
  // discard the cached analysis so the composer can re-analyze revised content.
  // Falls back to handleClickClose when not supplied (e.g. EditRewrite). TRIL-274.
  handleReviseStory?: () => Promise<void> | void;
  handleAcceptAnalysis: () => Promise<void>;
  analysisResult: AnalysisResponse;
  draftTextSentiment: SentimentResponse;
  handleEdit: () => Promise<void>;
  draftStoryAnalyzerPluginResults: any;
  jobId?: string | null;
  draftId?: string;
  userId?: string;
  createAnalysisLoading?: boolean;
}

// ─── Data normalizer (unchanged from original) ────────────────────────────────

interface NormalizedAnalysisResult {
  overall_score: number;
  plugin_results: Record<string, any>;
  user_recommendations: string[];
  metadata?: Record<string, any>;
  interpretations?: Record<string, any>;
}

function normalizeAnalysisResult(data: any): NormalizedAnalysisResult | null {
  if (!data) return null;

  if (data.result?.analysis) {
    const analysis = data.result.analysis;
    const warnings = data.result.warnings ?? [];
    return {
      overall_score: analysis.overall_score ?? 0,
      plugin_results: analysis.plugin_results ?? {},
      user_recommendations: generateReplyRecommendations(analysis, warnings),
      metadata: analysis.metadata,
      interpretations: analysis.interpretations,
    };
  }

  if (data.analysis?.analysis_type === 'reply') {
    const analysis = data.analysis;
    const warnings = data.warnings ?? [];
    return {
      overall_score: analysis.overall_score ?? 0,
      plugin_results: analysis.plugin_results ?? {},
      user_recommendations: generateReplyRecommendations(analysis, warnings),
      metadata: analysis.metadata,
      interpretations: analysis.interpretations,
    };
  }

  if (data.analysis_type === 'reply') {
    return {
      overall_score: data.overall_score ?? 0,
      plugin_results: data.plugin_results ?? {},
      user_recommendations: generateReplyRecommendationsFromAnalysis(data),
      metadata: data.metadata,
      interpretations: data.interpretations,
    };
  }

  return {
    overall_score: data.overall_score ?? 0,
    plugin_results: data.plugin_results ?? {},
    user_recommendations: data.user_recommendations ?? [],
    metadata: data.metadata,
    interpretations: data.interpretations,
  };
}

function generateReplyRecommendations(
  analysis: any,
  warnings: any[],
): string[] {
  const recommendations: string[] = [];
  if (warnings && Array.isArray(warnings)) {
    warnings.forEach((warning: any) => {
      if (typeof warning === 'string') {
        recommendations.push(`⚠️ ${warning}`);
      } else if (warning.message) {
        const icon =
          warning.level === 'high'
            ? '🚨'
            : warning.level === 'medium'
              ? '⚠️'
              : 'ℹ️';
        recommendations.push(`${icon} ${warning.message}`);
      }
    });
  }
  if (analysis?.interpretations) {
    const { flags, recommendations: interpRecs } = analysis.interpretations;
    if (flags && Array.isArray(flags)) {
      flags.forEach((flag: string) => {
        if (!recommendations.includes(flag)) recommendations.push(flag);
      });
    }
    if (interpRecs && Array.isArray(interpRecs)) {
      interpRecs.forEach((rec: string) => {
        if (!recommendations.includes(rec)) recommendations.push(rec);
      });
    }
  }
  const score = analysis?.overall_score ?? 0;
  if (score >= 80)
    recommendations.unshift('✅ High quality reply - ready to publish!');
  else if (score >= 60) recommendations.unshift('👍 Good quality reply');
  else if (score >= 40)
    recommendations.unshift('📝 Consider revising your reply');
  else recommendations.unshift('⚠️ Reply may need significant revision');
  return recommendations;
}

function generateReplyRecommendationsFromAnalysis(analysis: any): string[] {
  const recommendations: string[] = [];
  if (analysis?.interpretations) {
    const {
      flags,
      recommendations: interpRecs,
      overall_quality,
    } = analysis.interpretations;
    if (overall_quality) recommendations.push(`📊 ${overall_quality}`);
    if (flags && Array.isArray(flags))
      flags.forEach((flag: string) => recommendations.push(flag));
    if (interpRecs && Array.isArray(interpRecs))
      interpRecs.forEach((rec: string) => recommendations.push(rec));
  }
  const toxicity = analysis?.toxicity?.TOXIC ?? 0;
  if (toxicity > 0.5)
    recommendations.push(
      `🚨 High toxicity detected (${Math.round(toxicity * 100)}%) - please revise`,
    );
  else if (toxicity > 0.3)
    recommendations.push(
      `⚠️ Moderate toxicity detected (${Math.round(toxicity * 100)}%)`,
    );
  return recommendations;
}

// ─── Component ────────────────────────────────────────────────────────────────

export const TextAnalysisResponse = (props: TextAnalysisResponseProps) => {
  const {
    isModalOpen,
    hasStoryAnalysis,
    isReAnalyze,
    handleClickClose,
    handleReviseStory,
    analysisResult,
    draftTextSentiment,
    handleAcceptAnalysis,
    createAnalysisLoading,
    draftStoryAnalyzerPluginResults,
    jobId,
    draftId,
    userId,
  } = props;

  const { SAVE_ANALYSIS, REVISE_STORY } = DRAFT.ANALYSIS_DIALOG;

  // ── Normalise sentiment ──────────────────────────────────────────────────
  const normalizedSentiment = draftTextSentiment
    ? {
        label: draftTextSentiment.label ?? draftTextSentiment.primary,
        score: draftTextSentiment.score ?? draftTextSentiment.confidence,
        scores:
          draftTextSentiment.scores ?? draftTextSentiment.sentiment_scores,
        confidence_explanation: draftTextSentiment.confidence_explanation,
        contribution_explanation: draftTextSentiment.contribution_explanation,
        sentiment_summary:
          draftTextSentiment.sentiment_summary &&
          draftTextSentiment.sentiment_summary !== 'Unable to analyze sentiment'
            ? draftTextSentiment.sentiment_summary
            : null,
      }
    : null;

  const {
    confidence_explanation,
    contribution_explanation,
    sentiment_summary,
  } = normalizedSentiment ?? {};

  // ── Normalise result ─────────────────────────────────────────────────────
  const rawData = analysisResult ?? draftStoryAnalyzerPluginResults;
  const normalizedResult = normalizeAnalysisResult(rawData);

  const overallScore = normalizedResult?.overall_score ?? 0;
  const pluginResults = normalizedResult?.plugin_results ?? {};
  const blocksPublish = pluginResults?.self_harm?.blocks_publish === true;
  const userRecommendations = normalizedResult?.user_recommendations ?? [];
  const emotionScores = pluginResults?.emotion_detection?.emotion_scores;

  const topicRelevance = pluginResults?.topic_relevance;
  const primaryTopic =
    topicRelevance?.primary_topic ?? topicRelevance?.topics?.[0]?.label ?? null;
  const topicConfidence =
    topicRelevance?.topic_confidence ??
    topicRelevance?.topics?.[0]?.score ??
    null;
  const categoryScores = topicRelevance?.category_scores;

  const recommendationsList =
    userRecommendations.length > 0
      ? userRecommendations.map((rec: string, index: number) => (
          <li key={index} className="text-sm text-gray-700">
            • {rec}
          </li>
        ))
      : [
          <li key="none" className="text-sm text-gray-500">
            No recommendations
          </li>,
        ];

  // ── Tab: Analysis body ───────────────────────────────────────────────────
  const analysisBody = (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        gap: '1rem',
        justifyContent: 'space-between',
        alignItems: 'center',
      }}
      className={'STORY__ANALYSIS__DIALOG__BODY'}
    >
      <SafetyPanel category={pluginResults?.self_harm?.category} />
      <div
        style={{
          display: 'flex',
          flexDirection: 'row',
          gap: '1rem',
          justifyContent: 'space-between',
          alignItems: 'center',
        }}
        className={'ANALYSIS__DIALOG__BODY__METRICS'}
      >
        {normalizedSentiment && (
          <SentimentAnalysis data={normalizedSentiment} />
        )}
        {normalizedSentiment && <SentimentDisplay data={normalizedSentiment} />}
        {emotionScores && <TopEmotions data={emotionScores} />}
      </div>

      {primaryTopic && (
        <div>
          <p>Topic Classification</p>
          <p style={{ fontWeight: 'bold', textTransform: 'capitalize' }}>
            {primaryTopic.replace(/_/g, ' ')}
            {topicConfidence && (
              <span style={{ marginLeft: '0.25rem' }}>
                ({Math.round(topicConfidence * 100)}% confidence)
              </span>
            )}
          </p>
          {categoryScores && (
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.25rem' }}>
              {Object.entries(categoryScores)
                .sort(([, a], [, b]) => (b as number) - (a as number))
                .map(([label, score]) => (
                  <small
                    key={label}
                    style={{
                      padding: '0.125rem 0.5rem',
                      borderRadius: '4px',
                      backgroundColor:
                        label === primaryTopic ? '#1976d2' : '#f5f5f5',
                      color: label === primaryTopic ? '#fff' : '#666',
                      textTransform: 'capitalize',
                    }}
                  >
                    {label.replace(/_/g, ' ')}{' '}
                    {Math.round((score as number) * 100)}%
                  </small>
                ))}
            </div>
          )}
        </div>
      )}

      {confidence_explanation && <div>{confidence_explanation}</div>}
      {contribution_explanation && <div>{contribution_explanation}</div>}
      {sentiment_summary && <div>{sentiment_summary}</div>}

      {pluginResults?.story_summary && (
        <QuickSummary data={pluginResults.story_summary} />
      )}

      <div className="mb-6 w-full">
        <h3 className="font-semibold mb-2">Recommendations</h3>
        <ul className="space-y-1">{recommendationsList}</ul>
      </div>
    </div>
  );
  console.log('jobId', jobId, 'draftId', draftId, 'userId', userId);

  // ── Tabs config (identical structure to original) ────────────────────────
  const analysisModalTabs = [
    {
      id: 'storyAnalysisTab',
      label: 'Analysis',
      content: analysisBody,
    },
    {
      id: 'analysisFeedbackFormTab',
      label: 'Analysis Feedback',
      content: (
        <StoryAnalyzerFeedbackForm
          jobId={jobId as string}
          storyId={null}
          draftId={draftId as string}
          userId={userId as string}
          detectedTopic={primaryTopic ?? null}
          detectedSentiment={normalizedSentiment?.label ?? null}
        />
      ),
    },
  ];

  return (
    <>
      {isModalOpen && (
        <div className="drawer-overlay" onClick={handleClickClose} />
      )}
      <div className={isModalOpen ? 'drawer drawer--open' : 'drawer'}>
        {/* ── Header — reuses existing OVERALL__SCORE__* CSS class for colour ── */}
        <div
          className={`OVERALL__SCORE__${getScoreColor(overallScore)}`}
          style={{
            padding: '1rem 1.5rem',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            flexShrink: 0,
          }}
        >
          <DialogHeader
            analysisResult={
              {
                ...normalizedResult,
                overall_score: overallScore,
              } as any
            }
          />
          <button
            type="button"
            aria-label="close analysis drawer"
            onClick={handleClickClose}
            style={{
              background: 'none',
              border: 'none',
              cursor: 'pointer',
              fontSize: '1.25rem',
            }}
          >
            ✕
          </button>
        </div>

        {/* ── Scrollable body ── */}
        <div className="drawer__scrollable-body">
          <BasicTabs tabs={analysisModalTabs} defaultValue={0} />
        </div>

        {/* ── Sticky footer ── */}
        <div>
          <hr />
          <div
            style={{
              padding: '1rem 1.5rem',
              display: 'flex',
              justifyContent: 'flex-end',
              gap: '1rem',
            }}
          >
            {blocksPublish ? (
              <button
                type="button"
                className="btn btn--primary"
                onClick={handleReviseStory ?? handleClickClose}
              >
                {REVISE_STORY}
              </button>
            ) : (
              <>
                {(!hasStoryAnalysis || isReAnalyze) && (
                  <button
                    type="button"
                    className="btn btn--text"
                    onClick={handleClickClose}
                  >
                    Cancel
                  </button>
                )}
                <button
                  type="button"
                  className="btn btn--primary"
                  onClick={
                    isReAnalyze || !hasStoryAnalysis
                      ? handleAcceptAnalysis
                      : handleClickClose
                  }
                >
                  {isReAnalyze
                    ? 'Accept New Analysis'
                    : analysisResult && !hasStoryAnalysis
                      ? SAVE_ANALYSIS
                      : POST.CLOSE_MODAL}
                </button>
              </>
            )}
          </div>
        </div>
      </div>
      {createAnalysisLoading && <SimpleBackdrop open={createAnalysisLoading} />}
    </>
  );
};
